import request from '../request';

import type { AxiosResponse } from 'axios';
import type {
  IChangePassword,
  ICreateStory,
  IRateData,
  IStoryData,
  IStoryHashtag,
  IStoryHashtagParams,
  IStoryList,
  IStoryListParams,
  IUpdateStoryVisibility,
  IStoryAnalysis,
  IUpdateStoryContent,
  IGetStoryVoice,
  IGetStoryTitlesParams,
  IStoryTitleSuggestion,
  IUpdateStoryNameParams,
  IGetStoryVideoDownloadParams,
  IStoryVideoDownloadResponse,
  IGetStoryHashtagsParams,
  IStoryHashtagResponse,
  IGetStoryRequestInfoParams,
  IStoryRequestInfo,
  IGetRecommendedStoriesParams,
  IGetWhatIfStoryVoice,
} from './type';
import type { IService } from '../../infrastructure';

export const createStoryService = async (
  storyRequestData: ICreateStory,
): Promise<IStoryData> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<ICreateStory> = {
    data: storyRequestData,
    method: 'POST',
    relativePath: 'create-story-multiimage',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryData>,
    ICreateStory
  >(data);
  return response.data;
};
export const getStoriesService = async (
  params: IStoryListParams,
): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IStoryListParams> = {
    method: 'POST',
    data: params,
    relativePath: 'get-story-list-hashtag',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryList[]>,
    IStoryListParams
  >(data);
  return response.data;
};

export const getHashtagList = async (
  params: IStoryHashtagParams,
): Promise<IStoryHashtag[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IStoryHashtagParams> = {
    method: 'POST',
    data: params,
    relativePath: 'get-hashtag',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryHashtag[]>,
    IStoryHashtagParams
  >(data);
  return response.data;
};

export const getUserStoriesService = async (): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<ICreateStory> = {
    method: 'GET',
    relativePath: 'get-story-list',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryList[]>,
    ICreateStory
  >(data);
  return response.data;
};

export const getPublicStoriesService = async (): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<ICreateStory> = {
    method: 'GET',
    relativePath: 'get-story-list',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryList[]>,
    ICreateStory
  >(data, false);
  return response.data;
};

export const getUserStoryDetailsService = async (
  storyId: number,
): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<ICreateStory> = {
    method: 'GET',
    relativePath: `get-story-view/${storyId}`,
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryList[]>,
    ICreateStory
  >(data);
  return response.data;
};

export const getStoryVoice = async ({
  story_id,
  story_content_id,
  story_vorder,
  voice_speaker,
  voice_vibe,
}: IGetStoryVoice): Promise<string> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService = {
    method: 'post',
    relativePath: `get-voice-story`,
    headers: header,
    data: {
      story_id,
      story_content_id,
      story_vorder,
      voice_speaker,
      voice_vibe,
    },
  };
  const response = await request.requestService<AxiosResponse<string>>(data);
  return response.data;
};
export const getWhatIfStoryVoice = async ({
  story_id,
  story_content_id,
  contentVOrder,
}: IGetWhatIfStoryVoice): Promise<string> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService = {
    method: 'post',
    relativePath: `get-whatif-voice-story`,
    headers: header,
    data: {
      story_id,
      story_content_id,
      contentVOrder,
    },
  };
  const response = await request.requestService<AxiosResponse<string>>(data);
  return response.data;
};

export const getSuggestedStoryService = async (): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService = {
    method: 'GET',
    relativePath: `get-top-story`,
    headers: header,
  };
  const response =
    await request.requestService<AxiosResponse<IStoryList[]>>(data);
  return response.data;
};

export const getStoryInSights = async (
  params: IGetStoryRequestInfoParams,
): Promise<any> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };

  const data: IService = {
    method: 'POST',
    relativePath: `get-story-insights`,
    headers: header,
    data: params,
  };
  const response = await request.requestService<AxiosResponse<any>>(data);
  return response.data;
};

export const changePassword = async (
  formData: IChangePassword,
): Promise<boolean> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IChangePassword> = {
    data: formData,
    method: 'POST',
    relativePath: 'trust/change-password',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<boolean>,
    IChangePassword
  >(data);
  return response.data;
};

export const rateStory = async (rateData: IRateData): Promise<any> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IRateData> = {
    data: rateData,
    method: 'POST',
    relativePath: 'rate-story',
    headers: header,
  };
  const response = await request.requestService<AxiosResponse<any>, IRateData>(
    data,
  );
  return response.data;
};

export const saveStory = async (storyId: string): Promise<string> => {
  const data: IService = {
    method: 'GET',
    relativePath: `save-story/${storyId}`,
  };
  const response = await request.requestService<AxiosResponse<string>>(data);
  return response.data;
};

export const unSaveStory = async (storyId: string): Promise<string> => {
  const data: IService = {
    method: 'GET',
    relativePath: `remove-story-saved/${storyId}`,
  };
  const response = await request.requestService<AxiosResponse<string>>(data);
  return response.data;
};

export const updateStoryVisibility = async (
  visibilityData: IUpdateStoryVisibility,
): Promise<boolean> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IUpdateStoryVisibility> = {
    data: visibilityData,
    method: 'POST',
    relativePath: 'update-story-visibility',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<boolean>,
    IUpdateStoryVisibility
  >(data);
  return response.data;
};

export const getStoryAnalysis = async (
  storyId: number,
): Promise<IStoryAnalysis[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService = {
    method: 'POST',
    relativePath: 'get-story-analysis',
    headers: header,
    data: { story_id: storyId },
  };
  const response =
    await request.requestService<AxiosResponse<IStoryAnalysis[]>>(data);
  return response.data;
};

export const updateStoryContent = async (
  contentData: IUpdateStoryContent,
): Promise<boolean> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IUpdateStoryContent> = {
    data: contentData,
    method: 'POST',
    relativePath: 'update-story-content',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<boolean>,
    IUpdateStoryContent
  >(data);
  return response.data;
};

export const getStoryTitles = async (
  params: IGetStoryTitlesParams,
): Promise<IStoryTitleSuggestion[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IGetStoryTitlesParams> = {
    data: params,
    method: 'POST',
    relativePath: 'get-story-titles',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryTitleSuggestion[]>,
    IGetStoryTitlesParams
  >(data);
  return response.data;
};

export const updateStoryName = async (
  params: IUpdateStoryNameParams,
): Promise<boolean> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IUpdateStoryNameParams> = {
    data: params,
    method: 'POST',
    relativePath: 'update-Story-name',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<boolean>,
    IUpdateStoryNameParams
  >(data);
  return response.data;
};

export const getStoryVideoDownload = async (
  params: IGetStoryVideoDownloadParams,
): Promise<IStoryVideoDownloadResponse> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IGetStoryVideoDownloadParams> = {
    data: params,
    method: 'POST',
    relativePath: 'get-story-video-download',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryVideoDownloadResponse>,
    IGetStoryVideoDownloadParams
  >(data);
  return response.data;
};

export const getStoryHashtags = async (
  params: IGetStoryHashtagsParams,
): Promise<IStoryHashtagResponse[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IGetStoryHashtagsParams> = {
    data: params,
    method: 'POST',
    relativePath: 'get-story-hashtags',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryHashtagResponse[]>,
    IGetStoryHashtagsParams
  >(data);
  return response.data;
};

export const getStoryRequestInfo = async (
  params: IGetStoryRequestInfoParams,
): Promise<IStoryRequestInfo[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IGetStoryRequestInfoParams> = {
    data: params,
    method: 'POST',
    relativePath: 'get-story-request-info',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryRequestInfo[]>,
    IGetStoryRequestInfoParams
  >(data);
  return response.data;
};

export const getRecommendedStories = async (
  params: IGetRecommendedStoriesParams,
): Promise<IStoryList[]> => {
  const header = { 'Content-Type': 'application/json; charset=utf-8' };
  const data: IService<IGetRecommendedStoriesParams> = {
    data: params,
    method: 'POST',
    relativePath: 'get-recommended-story-list-of-one-story',
    headers: header,
  };
  const response = await request.requestService<
    AxiosResponse<IStoryList[]>,
    IGetRecommendedStoriesParams
  >(data);
  return response.data;
};
